import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { pickBy } from "lodash";
import {
  defer,
  forkJoin,
  from,
  iif,
  lastValueFrom,
  Observable,
  of,
  throwError
} from "rxjs";
import { catchError, switchMap } from "rxjs/operators";
import { FiscalProfileBackend } from "src/app/backend/api/fiscal-profile.backend";
import { LetterheadBackend } from "src/app/backend/api/letterhead/letterhead.backend";
import { StorageBackend } from "src/app/backend/api/storage/storage.backend";
import {
  PassiveInvoice,
  PassiveInvoicePayment,
  PassiveInvoicePaymentCreate
} from "src/app/backend/models/passive-invoice/passive-invoice";
import { PassiveInvoiceBadCredentialsError } from "src/app/errors/passive-invoice-bad-credentials.error";
import { PassiveInvoiceUnknowError } from "src/app/errors/passive-invoice-unknow.error";
import { RegionService } from "src/app/services/feature-access-control/region.service";
import { PrintService } from "src/app/services/print/print.service";
import { TemplateService } from "src/app/services/template.service";
import { blobToDataUrl } from "src/app/support/blob";
import { formatCurrency } from "src/app/support/currency";
import { dateUtil } from "src/app/support/date-util";
import { UserNotify } from "src/app/support/notify-decorator";
import { PageRequest } from "src/app/support/page-request";
import { PaginatedResult } from "src/app/support/paginated-result";
import { pdfMergeDocuments } from "src/app/support/pdf";
import { sntTranslate } from "src/app/support/translate";
import { xlstToHtml } from "src/app/support/xlst";
import { environment } from "src/environments/environment";

@Injectable()
export class PassiveInvoiceBackend {
  private readonly http = inject(HttpClient);
  private readonly printService = inject(PrintService);
  private readonly templateService = inject(TemplateService);
  private readonly regionService = inject(RegionService);
  private readonly storageBackend = inject(StorageBackend);
  private readonly letterheadBackend = inject(LetterheadBackend);
  private readonly fiscalProfileBackend = inject(FiscalProfileBackend);

  load(passiveInvoiceId: string): Observable<PassiveInvoice> {
    return this.http.get<PassiveInvoice>(
      `${environment.snoots.api.billing}/passive-invoices/${passiveInvoiceId}`
    );
  }

  list(
    request: PageRequest & {
      select?: string | undefined;
    },
    filters: { fiscalProfileId?: string | undefined }
  ): Observable<PaginatedResult<PassiveInvoice>> {
    return this.http.get<PaginatedResult<PassiveInvoice>>(
      `${environment.snoots.api.billing}/passive-invoices`,
      {
        params: <any>pickBy({
          p: request.page,
          s: request.pageSize,
          fiscalProfileId: filters.fiscalProfileId,
          ...request.filterValues,
          select: request.select
        })
      }
    );
  }

  search(request: PageRequest): Observable<PaginatedResult<PassiveInvoice>> {
    return this.http.get<PaginatedResult<PassiveInvoice>>(
      `${environment.snoots.api.billing}/passive-invoices`,
      {
        params: pickBy({
          p: request.page,
          s: request.pageSize,
          ...request.filterValues
        })
      }
    );
  }

  @UserNotify({
    successMessage: marker("Fattura passiva aggiornata"),
    errorMessage: marker("Impossibile aggiornare la fattura passiva")
  })
  update(id: string, request: Partial<PassiveInvoice>): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.billing}/passive-invoices/${id}`,
      request
    );
  }

  @UserNotify({
    successMessage: marker("Fattura saldata"),
    errorMessage: marker("Impossibile saldare la fattura")
  })
  paid(id: string): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.billing}/passive-invoices/${id}/paid`,
      {}
    );
  }

  @UserNotify({
    successMessage: marker("Fattura da saldare"),
    errorMessage: marker("Impossibile modificare la fattura")
  })
  unpaid(id: string): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.billing}/passive-invoices/${id}/unpaid`,
      {}
    );
  }

  @UserNotify({
    successMessage: marker("Sincronizzazione fatture passive avviata"),
    errorMessage: marker(
      "Impossibile avviare la sincronizzazione delle fatture passive"
    )
  })
  download(
    fiscalProfileId: string,
    forced: boolean,
    dateFrom: Date | undefined
  ): Observable<void> {
    return this.http
      .put<void>(
        `${environment.snoots.api.billing}/electronic-invoice/passive-invoices/download`,
        { fiscalProfileId: fiscalProfileId, forced: forced, dateFrom: dateFrom }
      )
      .pipe(
        catchError((errorResponse: HttpErrorResponse) => {
          switch (errorResponse.status) {
            case 401: {
              return throwError(new PassiveInvoiceBadCredentialsError());
            }
            default: {
              return throwError(new PassiveInvoiceUnknowError());
            }
          }
        })
      );
  }

  @UserNotify({
    successMessage: marker("Fattura exportata in XML"),
    errorMessage: marker("Impossibile esportare la fattura")
  })
  exportXML(id: string): Observable<{ content: string; fileName: string }> {
    return this.http.put<{ content: string; fileName: string }>(
      `${environment.snoots.api.billing}/electronic-invoice/invoices/${id}/export`,
      {}
    );
  }

  @UserNotify({
    successMessage: marker("Fatture passive esportate"),
    errorMessage: marker("Impossibile esportare le fatture passive")
  })
  export(exportOptions: {
    filter?: Record<string, any>;
    includeIds?: string[];
    excludeIds?: string[];
  }): Observable<{ content: string; fileName: string }> {
    return this.http.post<{ content: string; fileName: string }>(
      `${environment.snoots.api.billing}/passive-invoices/exports`,
      {
        type: "passive-invoice",
        ...exportOptions
      }
    );
  }

  @UserNotify({
    successMessage: marker("Pagamento inserito"),
    errorMessage: (error: any) => {
      return sntTranslate(marker("Impossibile inserire il pagamento"));
    }
  })
  addPayment(
    invoiceId: string,
    payment: PassiveInvoicePaymentCreate
  ): Observable<void> {
    return this.http.post<void>(
      `${environment.snoots.api.billing}/passive-invoices/${invoiceId}/payments`,
      payment
    );
  }

  @UserNotify({
    successMessage: marker("Pagamento aggiornato"),
    errorMessage: marker("Impossibile aggiornare il pagamento")
  })
  updatePayment(
    invoiceId: string,
    itemId: string,
    payment: Partial<PassiveInvoicePayment>
  ): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.billing}/passive-invoices/${invoiceId}/payments/${itemId}`,
      payment
    );
  }

  @UserNotify({
    successMessage: marker("Pagamento rimosso"),
    errorMessage: marker("Impossibile rimuovere il pagamento")
  })
  deletePayment(invoiceId: string, itemId: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.snoots.api.billing}/passive-invoices/${invoiceId}/payments/${itemId}`,
      {}
    );
  }

  updateCredentials({
    fiscalProfileId,
    username,
    password
  }: {
    fiscalProfileId: string;
    username: string;
    password: string;
  }): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.billing}/fiscal-profiles/${fiscalProfileId}`,
      {
        electronicInvoice: {
          username,
          password
        }
      }
    );
  }

  async print(invoiceId: string): Promise<void> {
    try {
      const invoice = await lastValueFrom(this.load(invoiceId));

      await this.printService.printPdf(this.toPdf(invoice));
    } catch (ex) {
      console.error(ex);
      throw ex;
    }
  }

  async toPdf(invoice: PassiveInvoice): Promise<Blob> {
    let invoiceXmlToHtml: string = "";

    const {
      xmlStorageId,
      sender,
      withholdingTaxAmount,
      paymentsData,
      paid,
      total
    } = invoice;

    const senderName = sender?.name ?? "-";
    const senderVatNumber = sender?.vatNumber ?? "-";
    const number: string = sender?.invoiceNumber ?? "####";
    const invoiceEmittedAt: Date | undefined = sender?.invoiceEmittedAt;

    const [
      passiveInvoiceTemplate,
      tailwindCss,
      passiveInvoiceHeaderTemplate,
      invoiceFooterTemplate,
      defaultLetterhead,
      fiscalProfile,
      getPaymentMethod
    ] = await Promise.all([
      fetch("/assets/ejs-templates/passive-invoice/passive-invoice.ejs").then(
        (response) => response.text()
      ),
      fetch("/assets/ejs-templates/passive-invoice/tailwind.css").then(
        (response) => response.text()
      ),
      fetch("/assets/ejs-templates/header.ejs").then((response) =>
        response.text()
      ),
      fetch(
        "/assets/ejs-templates/passive-invoice/passive-invoice-footer.ejs"
      ).then((response) => response.text()),
      lastValueFrom(this.letterheadBackend.loadDefaultLight()),
      lastValueFrom(
        iif(
          () => !!invoice.recipient?.fiscalProfileId,
          this.fiscalProfileBackend.load(invoice.recipient?.fiscalProfileId!),
          of(undefined)
        )
      ),
      import("src/app/support/payment-method").then(
        (mod) => mod.getPaymentMethod
      )
    ]);

    const { defaultLogoData, defaultSecondaryImageData } = await lastValueFrom(
      forkJoin({
        defaultLogoData: iif(
          () => !!defaultLetterhead?.header?.logo?.id,
          defer(() =>
            this.storageBackend
              .download(defaultLetterhead?.header!.logo!.id!)
              .pipe(
                switchMap((downloadUrl: string) =>
                  from(
                    fetch(downloadUrl)
                      .then((response) => response.blob())
                      .then((blob) => new Blob([blob], { type: "image/png" }))
                      .then((imageBlob) => blobToDataUrl(imageBlob))
                  )
                ),
                catchError(() => of(undefined))
              )
          ),
          of(undefined)
        ),
        defaultSecondaryImageData: iif(
          () => !!defaultLetterhead?.header?.secondaryImage?.id,
          defer(() =>
            this.storageBackend
              .download(defaultLetterhead?.header!.secondaryImage!.id!)
              .pipe(
                switchMap((downloadUrl: string) =>
                  from(
                    fetch(downloadUrl)
                      .then((response) => response.blob())
                      .then((blob) => new Blob([blob], { type: "image/png" }))
                      .then((imageBlob) => blobToDataUrl(imageBlob))
                  )
                ),
                catchError(() => of(undefined))
              )
          ),
          of(undefined)
        )
      })
    );

    const htmlHeadeRendered = this.templateService.render(
      passiveInvoiceHeaderTemplate,
      {
        styles: "",
        content: defaultLetterhead?.header?.html || "",
        title: "",
        logo: defaultLogoData,
        secondaryImage: defaultSecondaryImageData,
        logoSize: defaultLetterhead?.header?.logo?.size || 1,
        secondaryImageSize:
          defaultLetterhead?.header?.secondaryImage?.size || 1,
        tailwindCss
      }
    );

    const printModel = {
      labels: {
        recipient: sntTranslate(marker("Destinatario")),
        supplier: sntTranslate(marker("Fornitore")),
        payment: sntTranslate(marker("Pagamento")),
        resume: sntTranslate(marker("Riepilogo")),
        vatNumber: sntTranslate(marker("P.IVA")),
        payments: sntTranslate(marker("Lista pagamenti")),
        businessName: sntTranslate(marker("Ragione sociale")),
        supplierName: sntTranslate(marker("Nome")),
        supplierInvoiceNumber: sntTranslate(marker("Numero fattura")),
        supplierEmittedDate: sntTranslate(marker("Data di emissione")),
        total: sntTranslate(marker("Totale")),
        withholdingTax: sntTranslate(marker("Ritenuta d'acconto")),
        paid: sntTranslate(marker("Pagata")),
        paidDate: sntTranslate(marker("Data di pagamento")),
        date: sntTranslate(marker("Data")),
        paidMethod: sntTranslate(marker("Metodo utilizzato"))
      },
      businessName: fiscalProfile?.businessName ?? "-",
      businessVatNumber: fiscalProfile?.vatNumber ?? "-",
      supplierName: senderName,
      senderVatNumber: senderVatNumber,
      invoiceNumber: number,
      invoiceEmittedAt: invoiceEmittedAt,
      total: formatCurrency({
        value: total ?? 0,
        regionService: this.regionService
      }),
      withholdingTaxAmount: formatCurrency({
        value: withholdingTaxAmount ?? 0,
        regionService: this.regionService
      }),
      paid: paid ? sntTranslate(marker("Si")) : sntTranslate(marker("No")),
      paidAt:
        paymentsData
          ?.map((paymentData) =>
            paymentData?.payments?.map((payment) => {
              if (paid) {
                return payment?.paidAt
                  ? dateUtil(payment.paidAt).format("DD/MM/YYYY")
                  : undefined;
              } else {
                return payment?.expiresAt
                  ? dateUtil(payment.expiresAt).format("DD/MM/YYYY")
                  : undefined;
              }
            })
          )
          .flat()
          .filter((d) => !!d)
          .join(", ") ?? "-",
      payments: paymentsData.flatMap((payments) =>
        payments.payments.map(({ amount, paidAt, expiresAt, method }) => ({
          amount: formatCurrency({
            value: amount,
            regionService: this.regionService
          }),
          date: dateUtil(paidAt ?? expiresAt).format("DD/MM/YYYY"),
          method: !!method ? (getPaymentMethod(method)?.display ?? "-") : "-"
        }))
      )
    };

    const htmlPassiveInvoiceRendered = this.templateService.render(
      passiveInvoiceTemplate,
      {
        ...printModel,
        tailwindCss
      }
    );

    let footerName: string = `${sntTranslate(marker("Fattura passiva n."))} ${number}`;

    if (!!invoiceEmittedAt) {
      footerName = `${footerName} ${sntTranslate(marker("del"))} ${dateUtil(
        invoiceEmittedAt
      ).format("DD/MM/YYYY")}`;
    }

    const htmlFooterRendered = this.templateService.render(
      invoiceFooterTemplate,
      {
        tailwindCss,
        footerName
      }
    );

    if (!!xmlStorageId) {
      const downloadUrl = await lastValueFrom(
        this.storageBackend.download(xmlStorageId, "attachment")
      );
      const xml = await (await fetch(downloadUrl)).text();
      invoiceXmlToHtml = await xlstToHtml({
        xml,
        xslTemplate: "/assets/xsl/Stampa_fattura_ordinaria_ver1.2.2.xsl"
      });
    }

    const pages = [
      {
        header: htmlHeadeRendered,
        footer: htmlFooterRendered,
        content: htmlPassiveInvoiceRendered
      },
      {
        header: "<!-- start: fattura elettronica -->",
        footer: "<!-- end: fattura elettronica -->",
        content: invoiceXmlToHtml,
        additionalOptions: {
          margin: {
            top: "0px",
            bottom: "0px",
            left: "0px",
            right: "0px"
          }
        }
      }
    ].map((page) => this.printService.htmlToPdf(page));
    return pdfMergeDocuments(await Promise.all(pages));
  }
}
